import * as React from 'react'
import {
  ChartArea,
  DollarSign,
  Truck,
  MoveUpRight,
  HandshakeIcon,
  Banknote,
  HandCoins,
  FileSearch,
  ShieldPlus,
  Landmark,
  Receipt,
  HelpCircle,
  Users
} from 'lucide-react'

import { NavMain } from '@/components/nav-main'
import NavUser from '@/components/nav-user'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from '@klear/ui/components/sidebar'
import { Logo } from '@/components/logo'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { getAdminBaseUrl } from '@klear/runtime-env'
import { Link, useParams } from 'react-router'
import toNumber from 'lodash/toNumber'
import { NavSecondary } from '@/components/nav-secondary'
import { useReceivables } from '@/app/receivables/use-receivables'
import {
  PERMISSION_ENTITY_CUSTOMER,
  PERMISSION_ENTITY_SUPPLIER,
  PERMISSION_ENTITY_TREASURY,
  PERMISSION_ENTITY_ORDER,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_USER,
  buildNavPathTrader,
  ROUTE_KLEAR_ACCOUNT,
  ROUTE_SUPPLIERS,
  ROUTE_IDENTITY_VERIFICATION,
  ROUTE_EXTERNAL_BANK,
  PERMISSION_ACTION_VIEW,
  ROUTE_DOCUMENTS,
  ROUTE_PAYABLES,
  ROUTE_RECEIVABLES,
  PERMISSION_ENTITY_PAYMENT,
  ROUTE_ORDERS,
  ROUTE_CUSTOMERS,
  ROUTE_BILLING,
  ROUTE_HELP_CENTER,
  ROUTE_TEAM
} from '@klear/constants'
const data = {
  klearAccount: [
    {
      title: 'Klear Financial Account',
      permission: PERMISSION_ENTITY_TREASURY,
      url: ROUTE_KLEAR_ACCOUNT,
      icon: DollarSign
    },
    {
      title: 'Receivables',
      permission: PERMISSION_ENTITY_INVOICE_OFFER,
      url: ROUTE_RECEIVABLES,
      icon: HandCoins
    },
    {
      title: 'Payables',
      permission: PERMISSION_ENTITY_PAYMENT,
      url: ROUTE_PAYABLES,
      icon: Banknote
    }
  ],
  capitalSolutions: [
    {
      title: 'Orders',
      permission: PERMISSION_ENTITY_ORDER,
      url: ROUTE_ORDERS,
      icon: ChartArea
    },
    {
      title: 'Customers',
      permission: PERMISSION_ENTITY_CUSTOMER,
      url: ROUTE_CUSTOMERS,
      icon: HandshakeIcon
    },
    {
      title: 'Suppliers',
      permission: PERMISSION_ENTITY_SUPPLIER,
      url: ROUTE_SUPPLIERS,
      icon: Truck
    }
  ],
  organization: [
    {
      title: 'Identity Verification',
      url: ROUTE_IDENTITY_VERIFICATION,
      icon: ShieldPlus
    },
    {
      title: 'External bank accounts',
      url: ROUTE_EXTERNAL_BANK,
      icon: Landmark
    },
    {
      title: 'Billing',
      url: ROUTE_BILLING,
      icon: Receipt
    },
    {
      title: 'Documents',
      url: ROUTE_DOCUMENTS,
      icon: FileSearch
    },
    {
      title: 'Team',
      url: ROUTE_TEAM,
      icon: Users,
      permission: PERMISSION_ENTITY_USER
    }
  ],
  navSecondary: [
    {
      title: 'go to Klear Admin app',
      url: `${getAdminBaseUrl()}/`,
      icon: MoveUpRight
    }
  ]
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth()
  const { traderId } = useParams()
  const canSeeKlearAccount = user.hasPermission(PERMISSION_ENTITY_TREASURY, PERMISSION_ACTION_VIEW)
  const canSeeReceivables = user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_VIEW)
  const { counts: receivableCounts } = useReceivables(toNumber(traderId), { enabled: canSeeReceivables })
  const visibleKlearAccountItems = data.klearAccount
    .filter((item) => (item.permission ? user.hasPermission(item.permission, PERMISSION_ACTION_VIEW) : true))
    .map((item) => (item.url === ROUTE_RECEIVABLES ? { ...item, badge: receivableCounts.total } : item))

  const organizationItems = data.organization.filter((item) => {
    if (item.url === ROUTE_BILLING && !canSeeKlearAccount) {
      return false
    }
    return true
  })

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <Link to={buildNavPathTrader(traderId!)}>
          <Logo />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        {visibleKlearAccountItems.length > 0 && <NavMain items={visibleKlearAccountItems} title="Cash Management" />}
        <NavMain items={data.capitalSolutions} title="Order Management" />
        <NavMain items={organizationItems} title="Organization Details" />
        <SidebarGroup>
          <SidebarGroupLabel>Support</SidebarGroupLabel>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Help Center">
                <a href={ROUTE_HELP_CENTER} target="_blank" rel="noopener noreferrer">
                  <HelpCircle />
                  <span>Help Center</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        {user.hasKlearRole && <NavSecondary items={data.navSecondary} className="mt-auto" />}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
